import type { BudgetChange, BudgetState, DisplaySettings } from "@gooner/shared";
import {
  DEFAULT_SETTINGS,
  DecodeBudget,
  DecodeSlots,
  PROBE_CODECS,
  PROBE_CONFIGS,
  displaySettings,
  effectiveDpr,
  isPhoneClass,
  minCeiling,
} from "@gooner/shared";
import { track } from "../analytics";
import { publicSettings } from "../lib/publicSettings";
import type { RegionRegistry } from "./types";

/**
 * THE WALL'S DECODE BUDGET (issues #13/#14, 2026-10-28) — the browser half; the rules are
 * DecodeBudget / DecodeSlots in shared, where tools/checks can drive them.
 *
 * The bottleneck on a weak device is decoding, not fetching: !26 measured 13–16 videos
 * decoding on a dense wall, and the errors browsers report ("PIPELINE_ERROR_DECODE",
 * "Failed to init decoder", "Media failed to decode") say the same. mediaWarm caps
 * FETCHES; nothing capped decodes, and every device was asked for the same load.
 *
 * Three layers, combined into one rung ceiling and one decode-slot limit:
 *
 *   1. Phones (#13): tiles are measured at a capped DPR and held under the admin's phone
 *      ceiling — a DPR-3 phone was asking for the 1920 rung.
 *   2. A one-time probe: MediaCapabilities.decodingInfo for VP9 at each rung. A rung the
 *      browser says it cannot play smoothly is off this session's ladder. Idle-time,
 *      never awaited by a tile, cached for the session.
 *   2b. A first-frame pass: a held tile with no frame of its own still decodes until it
 *      has one, a few at a time, then parks on it — nobody waits on a poster.
 *   2c. A staggered start: a display's tiles are admitted one at a time, biggest first,
 *      RAMP_MS apart — asking a device for twelve decoders in the same instant is exactly
 *      when "Failed to init decoder" happens. A full wall is playing within ~3 s.
 *   3. A live monitor: every MONITOR_MS, the dropped / total frame DELTAS of every tile
 *      that is decoding, fed to DecodeBudget — which lowers the ceiling a rung at a time,
 *      then the number of tiles allowed to decode at once (the rest hold their frame and
 *      take turns, see DecodeSlots), and lifts both slowly once playback is clean.
 *
 * One per display (keyed by its RegionRegistry, the same trick RegionPlayer's recent-shown
 * map uses), sticky for the session via sessionStorage. `settings.display.decodeBudget`
 * off = layers 2 and 3 do nothing; layer 1 is its own two settings.
 */

const MONITOR_MS = 2000;
const RAMP_MS = 250;
/** How many frameless tiles may decode at once for their FIRST frame, beyond the slots. */
const FIRST_FRAME_PASS = 4;
const STATE_KEY = "gp:playback-budget";
const PROBE_KEY = "gp:decode-probe";

function readSession<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
function writeSession(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota: the budget just isn't sticky */
  }
}

/** What this device is, for the wall: phone class and its raw pixel ratio. */
function device(): { phone: boolean; raw: number } {
  if (typeof window === "undefined") return { phone: false, raw: 1 };
  const coarse = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  const shortSide = Math.min(window.screen?.width ?? 0, window.screen?.height ?? 0);
  return { phone: isPhoneClass({ coarse, shortSide }), raw: window.devicePixelRatio || 1 };
}

/** The settings as of the one /api/settings fetch; the shipping defaults until it lands. */
let liveSettings: DisplaySettings = displaySettings(DEFAULT_SETTINGS);
const settingsReady = publicSettings().then((s) => {
  liveSettings = displaySettings(s);
  return liveSettings;
});

/** The pixel ratio a surface should measure at — the wall AND ClipViewer (one helper). */
export function deviceDpr(): { raw: number; effective: number; phone: boolean } {
  const d = device();
  return { raw: d.raw, effective: effectiveDpr(d.raw, d.phone, liveSettings), phone: d.phone };
}

type Listener = (granted: boolean) => void;

export class DisplayBudget {
  readonly budget: DecodeBudget;
  private slots = new DecodeSlots();
  private grants: Set<string> | null = null; // null = everyone may decode
  private listeners = new Map<string, Listener>();
  private last = new WeakMap<HTMLVideoElement, { dropped: number; total: number }>();
  private timer: ReturnType<typeof setInterval> | undefined;
  private lastRatio: number | null = null;
  /** The start-up ramp: how many tiles are admitted so far; null once everyone is. */
  private ramp: number | null = null;
  private rampTimer: ReturnType<typeof setInterval> | undefined;

  constructor(private readonly registry: RegionRegistry) {
    this.budget = new DecodeBudget(readSession<BudgetState>(STATE_KEY) ?? undefined);
    void settingsReady.then((s) => {
      if (s.decodeBudget) this.probe();
      else this.stopRamp();
      this.reallocate();
    });
  }

  /** The highest rung a tile may fetch now: phone ceiling, probe and live budget. */
  ceiling(): number | null {
    const s = liveSettings;
    const phone = device().phone;
    return minCeiling(
      phone ? s.phoneRungCeiling : null,
      s.decodeBudget ? this.budget.state.rungCeiling : null,
    );
  }

  maxDecoding(): number | null {
    return liveSettings.decodeBudget ? this.budget.state.maxDecoding : null;
  }

  granted(id: string): boolean {
    return this.grants == null || this.grants.has(id);
  }

  /** A tile listens for its own grant. Starts the monitor with the first tile. */
  subscribe(id: string, fn: Listener): () => void {
    if (this.listeners.size === 0) this.startRamp();
    this.listeners.set(id, fn);
    this.timer ??= setInterval(() => this.tick(), MONITOR_MS);
    this.reallocate();
    return () => {
      this.listeners.delete(id);
      if (this.listeners.size === 0) {
        if (this.timer) clearInterval(this.timer);
        this.timer = undefined;
        this.stopRamp();
      }
      this.reallocate();
    };
  }

  /** A tile's clip ended: its turn is over, to the back of the line. */
  yield(id: string): void {
    if (this.grants == null) return;
    this.slots.yield(id);
    this.reallocate();
  }

  /** A tile's decoder failed (MEDIA_ERR_DECODE / "Failed to init decoder"): the budget's
   *  second signal, for the failure that produces no frames for the monitor to count. */
  decodeFailure(): void {
    if (!liveSettings.decodeBudget) return;
    const tiles = [...this.listeners.keys()].filter((id) => this.registry.get(id)?.wantsToPlay?.() ?? true).length;
    this.apply(this.budget.decodeFailure({ tiles, now: Date.now() }));
  }

  /** Something about who wants to play changed (pause, scroll, page hidden). */
  poke(): void {
    this.reallocate();
  }

  /** The numbers media events carry, so a stall can be read against the budget. */
  eventProps(): Record<string, number | null> {
    const d = deviceDpr();
    return {
      dpr: Math.round(d.raw * 100) / 100,
      effective_dpr: Math.round(d.effective * 100) / 100,
      tiles: this.registry.size,
      rung_ceiling: this.ceiling(),
      max_decoding: this.maxDecoding(),
      dropped_ratio: this.lastRatio,
    };
  }

  /** Admit tiles one at a time from the first (biggest) on, RAMP_MS apart. */
  private startRamp(): void {
    this.stopRamp();
    if (!liveSettings.decodeBudget) return;
    this.ramp = 1;
    this.rampTimer = setInterval(() => {
      const wanting = [...this.listeners.keys()].filter((id) => this.registry.get(id)?.wantsToPlay?.() ?? true).length;
      if (this.ramp == null || this.ramp >= wanting) {
        this.stopRamp();
      } else this.ramp++;
      this.reallocate();
    }, RAMP_MS);
  }

  private stopRamp(): void {
    if (this.rampTimer) clearInterval(this.rampTimer);
    this.rampTimer = undefined;
    this.ramp = null;
  }

  private reallocate(): void {
    const max = minCeiling(this.maxDecoding(), this.ramp);
    let next: Set<string> | null = null;
    if (max != null) {
      const wanting: Array<{ id: string; bare: boolean; area: number }> = [];
      for (const [id, c] of this.registry) {
        if (!this.listeners.has(id)) continue; // a tile outside the budget (Rooms) always plays
        if (c.wantsToPlay?.() ?? true) wanting.push({ id, bare: c.bare?.() ?? false, area: c.area?.() ?? 0 });
      }
      next = this.slots.allocate(wanting, max, Date.now());
      // THE FIRST-FRAME PASS. A tile with no frame of its own (black, or a poster) may
      // decode until it has one, whatever the slot count — then the next reallocation
      // parks it on that frame (decoderPark). So every tile shows a real picture within
      // seconds, and the slots only decide which of them MOVE. Without this a tile held
      // from the start sat on its poster until its turn came round: 61 s on a dense wall
      // at 6x CPU, measured. A few at a time, so the pass is not itself a decoder rush.
      if (this.ramp == null) {
        let extra = 0;
        for (const w of wanting) {
          if (extra >= FIRST_FRAME_PASS) break;
          if (w.bare && !next.has(w.id)) {
            next.add(w.id);
            extra++;
          }
        }
      }
    }
    const before = this.grants;
    this.grants = next;
    for (const [id, fn] of this.listeners) {
      const was = before == null || before.has(id);
      const now = next == null || next.has(id);
      if (was !== now) fn(now);
    }
  }

  private apply(change: BudgetChange | null): void {
    if (!change) return;
    writeSession(STATE_KEY, this.budget.state);
    track("playback_budget_changed", {
      reason: change.reason,
      rung_ceiling: change.rungCeiling,
      max_decoding: change.maxDecoding,
      dropped_ratio: change.droppedRatio,
      tiles: change.tiles,
    });
    this.reallocate();
  }

  /** One sample: frame deltas over every tile that is decoding right now. */
  private tick(): void {
    if (!liveSettings.decodeBudget) return;
    let dropped = 0;
    let total = 0;
    let tiles = 0;
    let highestRung: number | null = null;
    for (const [id, c] of this.registry) {
      if (!this.granted(id) || !(c.wantsToPlay?.() ?? false)) continue;
      const v = c.video?.();
      if (!v || v.paused || typeof v.getVideoPlaybackQuality !== "function") continue;
      const q = v.getVideoPlaybackQuality();
      const prev = this.last.get(v);
      this.last.set(v, { dropped: q.droppedVideoFrames, total: q.totalVideoFrames });
      // Per ELEMENT, as deltas: the counters reset with a new source, and a fresh element
      // is a fresh entry. Backwards = a reset; that sample counts from zero.
      const dd = prev && q.droppedVideoFrames >= prev.dropped ? q.droppedVideoFrames - prev.dropped : q.droppedVideoFrames;
      const dt = prev && q.totalVideoFrames >= prev.total ? q.totalVideoFrames - prev.total : q.totalVideoFrames;
      if (!prev) continue; // first sight: baseline only
      dropped += dd;
      total += dt;
      tiles++;
      const r = c.rung?.() ?? null;
      if (r != null) highestRung = Math.max(highestRung ?? 0, r);
    }
    if (total > 0) this.lastRatio = Math.round((dropped / total) * 1000) / 1000;
    const outerCeiling = device().phone ? liveSettings.phoneRungCeiling || null : null;
    this.apply(this.budget.observe({ dropped, total, tiles, highestRung, outerCeiling, now: Date.now() }));
    // Slices expire on time, not only on events.
    if (this.maxDecoding() != null) this.reallocate();
  }

  /** Once per session, in idle time: which rungs does this browser say it decodes smoothly? */
  private probe(): void {
    const cached = readSession<Array<{ rung: number; smooth: boolean }>>(PROBE_KEY);
    if (cached) return; // already applied this session; the state carries it
    const mc = typeof navigator !== "undefined" ? navigator.mediaCapabilities : undefined;
    if (!mc?.decodingInfo) {
      writeSession(PROBE_KEY, []); // no API: no verdict, the monitor carries the weight
      return;
    }
    const ask = async (contentType: string, c: (typeof PROBE_CONFIGS)[number]) => {
      try {
        const info = await mc.decodingInfo({
          type: "file",
          video: { contentType, width: c.width, height: c.height, bitrate: c.bitrate, framerate: c.framerate },
        });
        return { rung: c.rung, supported: info.supported, smooth: info.supported && info.smooth, efficient: info.supported && info.powerEfficient };
      } catch {
        return null; // no verdict
      }
    };
    const top = (rs: Array<{ rung: number; [k: string]: unknown } | null>, key: "smooth" | "efficient") =>
      Math.max(0, ...rs.filter((r) => r && r[key]).map((r) => r!.rung));
    const run = async () => {
      const [vp9, h264] = await Promise.all([
        Promise.all(PROBE_CONFIGS.map((c) => ask(PROBE_CODECS.vp9, c))),
        Promise.all(PROBE_CONFIGS.map((c) => ask(PROBE_CODECS.h264, c))),
      ]);
      // An error is no verdict, which must not read as "can't": smooth, for the budget.
      const results = vp9.map((r, i) => r ?? { rung: PROBE_CONFIGS[i].rung, smooth: true });
      writeSession(PROBE_KEY, results);
      // What this device decodes smoothly and in hardware (powerEfficient is the browser's
      // stand-in for "hardware"), per codec: the highest rung, 0 for none. Once a session.
      track("decode_capabilities", {
        vp9_smooth_max: top(vp9, "smooth"),
        vp9_hw_max: top(vp9, "efficient"),
        h264_smooth_max: top(h264, "smooth"),
        h264_hw_max: top(h264, "efficient"),
      });
      this.apply(this.budget.applyProbe(results as Array<{ rung: number; smooth: boolean }>, this.registry.size));
    };
    const idle = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void })
      .requestIdleCallback;
    if (idle) idle(() => void run(), { timeout: 5000 });
    else setTimeout(() => void run(), 1500);
  }
}

const budgets = new WeakMap<RegionRegistry, DisplayBudget>();

/** The budget for one display (one RegionRegistry), made on first ask. */
export function budgetFor(registry: RegionRegistry): DisplayBudget {
  let b = budgets.get(registry);
  if (!b) {
    b = new DisplayBudget(registry);
    budgets.set(registry, b);
  }
  return b;
}
